import { useMemo, useState, useEffect, useCallback } from "react";
import { format, addMinutes, startOfDay, endOfDay, isToday, isYesterday, isTomorrow } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button, SegmentedTabs, FilterMenu, type FilterMenuGroup } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { SearchField } from "@akuna/ui";
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Play,
  Settings2,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  closestCorners,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { fetchAppointmentsForCsv, useCurrentClinicId, useAppointments, useUpdateAppointmentStatus, useVeterinarians, type AppointmentStatus } from "@/hooks/useAppointments";
import { getActiveClinicScope } from "@/lib/clinics/active-clinic-scope";
import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import { useAppointmentStatuses } from "@/hooks/useAppointmentStatuses";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useLocation, useNavigate } from "react-router-dom";
import {
} from "@/components/ui/dialog";
import { AppointmentFormResponsiveDialog } from "@/components/calendar/AppointmentFormResponsiveDialog";
import { QuickStartAttendanceDialog } from "@/components/calendar/QuickStartAttendanceDialog";
import { DroppableStatusColumn } from "@/components/calendar/DroppableStatusColumn";
import { AppointmentKanbanCard } from "@/components/calendar/AppointmentKanbanCard";
import { FlowboardList } from "@/components/calendar/FlowboardList";
import { MobileFlowboardHeader } from "@/components/calendar/mobile/MobileFlowboardHeader";
import { useFlowboardColumnVisibility } from "@/components/calendar/useFlowboardColumnVisibility";
import { buildFlowboardListColumns } from "@/components/calendar/flowboardListColumns";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useRole } from "@/hooks/useRole";
import { useProfile } from "@/hooks/useProfile";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@akuna/ui";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { downloadAppointmentsCsv, getAppointmentsCsvFilename } from "@/lib/exportAppointmentsToCsv";
import {
  saveLastCalendarView,
  saveLastFlowWorkspaceView,
} from "@/lib/utils/calendar-navigation";
import {
  persistFlowboardFilters,
  readPersistedFlowboardFilters,
} from "@/lib/utils/appointment-filters-storage";
import { toast } from "@/hooks/use-toast";
import { useIsVeterinarian } from "@/hooks/useIsVeterinarian";
import { matchesOwnerPetSearch } from "@/lib/patients/search";
import { getAppointmentTypeIcon, renderIconBySlug } from "@/lib/event-icons";
import { matchesAppointmentTypeFilter } from "@/lib/appointments/type";
import { getEffectiveAppointmentDriverType } from "@/lib/utils/appointment-driver";
import { BEHAVIOR_OPTIONS } from "@/lib/appointments/availability";
import { useClinicFeatureFlags } from "@/hooks/useClinicFeatureFlags";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { getFirstActiveStatus, getStatusColor } from "@/lib/utils/clinic-appointment-status";

const getDateRange = (date: Date) => ({
  start: startOfDay(date),
  end: endOfDay(date),
});

const getTypeIcon = (type: string) => {
  return renderIconBySlug(type, { tone: "white", sizeClass: "h-2.5 w-2.5" }) ?? getAppointmentTypeIcon(type, { tone: "white", sizeClass: "h-2.5 w-2.5" });
};

export default function Flowboard() {
  const { t, i18n } = useTranslation("calendar");
  const navigate = useNavigate();
  const location = useLocation();
  const initialFlowboardFilters = useMemo(() => readPersistedFlowboardFilters(), []);
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const { start, end } = getDateRange(selectedDate);
  const [view, setView] = useState<"kanban" | "list">(
    () => (localStorage.getItem("flowboard:view") === "list" ? "list" : "kanban"),
  );
  useEffect(() => {
    localStorage.setItem("flowboard:view", view);
  }, [view]);
  const { data: clinicId, isLoading: isClinicLoading, error: clinicError } = useCurrentClinicId();
  const agendaClinicIds = useMemo(
    () => getActiveClinicScope(clinicId),
    [clinicId],
  );
  const { data: appointments = [] } = useAppointments(clinicId, undefined, start, end, {
    variant: "list",
    clinicIds: agendaClinicIds,
  });
  const { data: listAppointments = [] } = useAppointments(clinicId, undefined, start, end, {
    variant: "full",
    clinicIds: agendaClinicIds,
    enabled: view === "list",
  });
  const { data: clinicFeatureFlags } = useClinicFeatureFlags();
  const { data: statuses = [], isLoading: isStatusesLoading } = useAppointmentStatuses();
  const listColumns = useMemo(() => buildFlowboardListColumns({ t, statuses }), [t, statuses]);
  const { visibility: listVisibility, toggleColumn: toggleListColumn, resetColumns: resetListColumns, visibleCount: listVisibleCount } = useFlowboardColumnVisibility(listColumns);
  const { data: veterinarians = [] } = useVeterinarians(clinicId);
  const { data: isVeterinarian } = useIsVeterinarian();
  const { data: clinicAppointmentTypes = [] } = useClinicAppointmentTypes();
  const updateStatus = useUpdateAppointmentStatus();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialFlowboardFilters.searchTerm);
  const [selectedVets, setSelectedVets] = useState<string[]>(initialFlowboardFilters.selectedVets);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialFlowboardFilters.selectedTypes);
  const [selectedDriverTypes, setSelectedDriverTypes] = useState<string[]>(initialFlowboardFilters.selectedDriverTypes);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isQuickStartOpen, setIsQuickStartOpen] = useState(false);
  const { hasPermission: canCreateMedicalRecord } = useHasPermissionSync("medical_records", "create");
  const { data: role } = useRole();
  const { data: profile } = useProfile();
  const hasCRMV = Boolean(
    profile?.crmv_state &&
    profile.crmv_state.trim() !== "" &&
    profile.crmv_number &&
    profile.crmv_number > 0,
  );
  const canShowQuickStart = canCreateMedicalRecord && (role === "veterinarian" || isVeterinarian) && hasCRMV;
  const locale = i18n.language.startsWith("en") ? enUS : ptBR;
  const professionalFilterOptions = useMemo(() => {
    const professionalsById = new Map<string, { id: string; full_name: string }>();

    veterinarians.forEach((vet) => {
      if (!vet.id || !vet.full_name?.trim()) return;
      professionalsById.set(vet.id, { id: vet.id, full_name: vet.full_name.trim() });
    });

    appointments.forEach((appointment) => {
      const vetUserId = appointment.vet_user_id;
      if (!vetUserId || professionalsById.has(vetUserId)) return;

      const joinedName = (appointment.vet as { full_name?: string | null } | null | undefined)?.full_name?.trim();
      if (joinedName) {
        professionalsById.set(vetUserId, { id: vetUserId, full_name: joinedName });
      }
    });

    return Array.from(professionalsById.values()).sort((left, right) =>
      left.full_name.localeCompare(right.full_name, "pt-BR", { sensitivity: "base" }),
    );
  }, [appointments, veterinarians]);
  const appointmentTypes = useMemo(
    () =>
      clinicAppointmentTypes.length > 0
        ? clinicAppointmentTypes.map((type) => ({
            value: type.id,
            label: type.name,
            color: type.color,
            icon: type.icon,
          }))
        : BEHAVIOR_OPTIONS.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
            color:
              option.value === "padrao" ? "#3b82f6" :
              option.value === "banho_tosa" ? "#22c55e" :
              option.value === "retorno" ? "#f59e0b" :
              option.value === "aplicacao" ? "#6366f1" :
              "#8b5cf6",
            icon: option.value,
          })),
    [clinicAppointmentTypes, t],
  );

  useEffect(() => {
    if (clinicAppointmentTypes.length === 0 || selectedTypes.length === 0) return;
    setSelectedTypes((prev) => {
      const migrated = prev.flatMap((value) => {
        const matchingTypeIds = clinicAppointmentTypes
          .filter((type) => type.behavior === value)
          .map((type) => type.id);
        return matchingTypeIds.length > 0 ? matchingTypeIds : [value];
      });
      const unique = Array.from(new Set(migrated));
      return unique.length === prev.length && unique.every((value, index) => value === prev[index])
        ? prev
        : unique;
    });
  }, [clinicAppointmentTypes, selectedTypes.length]);

  useEffect(() => {
    const state = (location.state as { openAppointmentId?: string } | null) ?? null;
    const openAppointmentId = state?.openAppointmentId;
    if (!openAppointmentId) return;

    setSelectedAppointmentId(openAppointmentId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // Save current view as last visited calendar view
  useEffect(() => {
    saveLastCalendarView("flowboard");
    saveLastFlowWorkspaceView("flowboard");
  }, []);

  useEffect(() => {
    persistFlowboardFilters({
      searchTerm,
      selectedVets,
      selectedTypes,
      selectedDriverTypes,
    });
  }, [searchTerm, selectedVets, selectedTypes, selectedDriverTypes]);

  const { visibleColumns, toggleColumn, isColumnVisible } = useColumnVisibility(statuses);

  // Configurar sensores com distance constraint
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  // Navegação de data
  const goToPreviousDay = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const goToNextDay = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handleCloseCreateDialog = () => {
    setIsCreateOpen(false);
    setTimeout(() => {
      // Limpar qualquer estado relacionado se necessário
    }, 300);
  };

  // Funções helper para calcular horários padrão
  const getCurrentTime = () => format(new Date(), "HH:mm");
  const getCurrentTimePlus30 = () => format(addMinutes(new Date(), 30), "HH:mm");

  // Calcular horários quando o diálogo é aberto
  const prefilledTime = useMemo(() => {
    return isCreateOpen ? getCurrentTime() : undefined;
  }, [isCreateOpen]);

  const prefilledEndTime = useMemo(() => {
    return isCreateOpen ? getCurrentTimePlus30() : undefined;
  }, [isCreateOpen]);

  // Filtrar agendamentos
  const matchesFilters = useCallback((appointment: AppointmentWithDetails) => {
    if (searchTerm) {
      const ownerName = appointment.owner?.name;
      const pets = (appointment.pets ?? []).length > 0 ? appointment.pets ?? [] : [appointment.pet];
      const hasPetMatch = pets.some((pet) =>
        matchesOwnerPetSearch({
          term: searchTerm,
          ownerName: ownerName as string | undefined,
          petName: (pet as { name?: string } | null)?.name,
          petRecordNumber: (pet as { record_number?: string } | null)?.record_number,
        }),
      );
      if (!hasPetMatch) return false;
    }
    if (selectedVets.length > 0 && appointment.vet_user_id) {
      if (!selectedVets.includes(appointment.vet_user_id)) return false;
    }
    if (!matchesAppointmentTypeFilter(appointment.type, selectedTypes, appointment.clinic_appointment_type?.behavior, appointment.clinic_appointment_type?.id)) {
      return false;
    }
    if (selectedDriverTypes.length > 0) {
      const effectiveType = getEffectiveAppointmentDriverType({
        driverType: appointment.taxi_dog_type,
        isTaxiDog: appointment.is_taxi_dog,
      });
      const matchesDriverType = selectedDriverTypes.some(
        (driverType) => effectiveType === driverType || effectiveType === "pickup_and_dropoff",
      );
      if (!matchesDriverType) return false;
    }
    return true;
  }, [searchTerm, selectedVets, selectedTypes, selectedDriverTypes]);

  const filteredAppointments = useMemo(
    () => appointments.filter(matchesFilters),
    [appointments, matchesFilters],
  );

  const filteredListAppointments = useMemo(
    () => listAppointments.filter(matchesFilters),
    [listAppointments, matchesFilters],
  );

  const toggleVet = (vetId: string) => {
    setSelectedVets((prev) =>
      prev.includes(vetId) ? prev.filter((id) => id !== vetId) : [...prev, vetId]
    );
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedVets([]);
    setSelectedTypes([]);
    setSelectedDriverTypes([]);
  };

  const activeFiltersCount =
    (searchTerm ? 1 : 0) +
    selectedVets.length +
    selectedTypes.length +
    selectedDriverTypes.length;

  const flowboardFilterGroups: FilterMenuGroup[] = [
    {
      id: "vets",
      title: t("filterByProfessional"),
      options: professionalFilterOptions.map((vet) => ({ label: vet.full_name, value: vet.id })),
      selected: selectedVets,
      onChange: setSelectedVets,
      emptyMessage: t("appointmentForm.noProfessionalResults"),
    },
    {
      id: "types",
      title: t("filterByType"),
      options: appointmentTypes.map((type) => ({ label: type.label, value: type.value })),
      selected: selectedTypes,
      onChange: setSelectedTypes,
    },
    {
      id: "driverTypes",
      title: t("filterByDriverType"),
      options: [
        { label: t("driverTypePickup"), value: "pickup" },
        { label: t("driverTypeDropoff"), value: "dropoff" },
      ],
      selected: selectedDriverTypes,
      onChange: setSelectedDriverTypes,
    },
  ];

  // Agrupar por status
  const groupedAppointments = useMemo(() => {
    const map: Record<string, AppointmentWithDetails[]> = {};

    statuses.forEach((status) => {
      map[status.name] = [];
    });

    filteredAppointments.forEach((appt) => {
      if (map[appt.status]) {
        map[appt.status].push(appt);
      } else {
        const fallbackStatus = getFirstActiveStatus(statuses, statuses[0]?.name ?? "agendado");
        if (fallbackStatus) {
          map[fallbackStatus.name] = map[fallbackStatus.name] ?? [];
          map[fallbackStatus.name].push(appt);
        }
      }
    });

    return map;
  }, [filteredAppointments, statuses]);

  // Status visíveis ordenados
  const visibleStatuses = useMemo(() => {
    return statuses
      .filter((status) => isColumnVisible(status.name))
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [statuses, isColumnVisible]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over || active.id === over.id) return;

    const statusName = over.id as string;
    const appointmentId = active.id as string;

    // Verificar se o status existe
    const targetStatus = statuses.find((s) => s.name === statusName);
    if (!targetStatus) return;

    updateStatus.mutate({ id: appointmentId, status: statusName as AppointmentStatus });
  };

  const activeAppointment = activeId
    ? appointments.find((a) => a.id === activeId) ?? null
    : null;

  // Formatação da data com indicador especial
  const dateLabel = format(selectedDate, "EEEE, d 'de' MMMM", { locale });
  const mobileDateLabel = format(selectedDate, "dd/MM");
  const isSelectedToday = isToday(selectedDate);
  const isSelectedYesterday = isYesterday(selectedDate);
  const isSelectedTomorrow = isTomorrow(selectedDate);

  const getDateIndicator = () => {
    if (isSelectedToday) return t("today");
    if (isSelectedYesterday) return t("yesterday");
    if (isSelectedTomorrow) return t("tomorrow");
    return null;
  };

  return (
    <PermissionGuard resource="appointments" action="view">
      <div className="space-y-3 md:space-y-3">
        <div className="hidden md:block">
          <div className="flex flex-col gap-4 px-5 sm:px-10 lg:px-20 container">
            <div className="flex md:flex-row flex-col justify-between items-center">
              <h1 className="hidden md:block text-3xl font-bold text-foreground">{t("flowboardTitle")}</h1>
              <div className="flex mt-4 md:mt-0 w-full md:w-auto md:flex-row flex-col gap-2">
                {canShowQuickStart ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={isClinicLoading || !clinicId}
                    onClick={() => setIsQuickStartOpen(true)}
                  >
                    <Play className="h-4 w-4" />
                    {t("startAttendance")}
                  </Button>
                ) : null}
                  <Button size="sm" className="gap-2" disabled={isClinicLoading || !clinicId} onClick={() => setIsCreateOpen(true)}>
                    <Plus className="h-4 w-4" />
                    {t("newAppointment")}
                  </Button>
                  <AppointmentFormResponsiveDialog
                    open={isCreateOpen}
                    onOpenChange={(open) => { if (!open) handleCloseCreateDialog(); else setIsCreateOpen(true); }}
                    clinicId={clinicId}
                    prefilledDate={selectedDate}
                    prefilledTime={prefilledTime}
                    prefilledEndTime={prefilledEndTime}
                    onSuccess={() => setIsCreateOpen(false)}
                  />
                {clinicId ? (
                  <QuickStartAttendanceDialog
                    clinicId={clinicId}
                    source="flowboard"
                    open={isQuickStartOpen}
                    onOpenChange={setIsQuickStartOpen}
                  />
                ) : null}
              </div>

            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Data em destaque */}
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg border bg-card/50">
                <CalendarDays className="h-5 w-5 text-primary" />
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-foreground">{dateLabel}</span>
                  {getDateIndicator() && (
                    <Badge variant={isSelectedToday ? "default" : "secondary"} className="text-xs">
                      {getDateIndicator()}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Controles de navegação */}
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousDay}
                  className="w-10 h-10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-10 h-10">
                      <CalendarDays className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setIsCalendarOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextDay}
                  className="w-10 h-10"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

              </div>
            </div>
          </div>
        </div>

        {clinicError && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-5 sm:px-10 lg:px-20 container py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{clinicError.message || t("loadClinicError")}</span>
          </div>
        )}

        <div className="hidden md:block">
          {/* Barra de Filtros */}
          <div className="flex flex-wrap items-center gap-3 px-5 sm:px-10 lg:px-20 container">
            <div className="flex-1 min-w-[220px] max-w-md">
              <SearchField
                label={t("searchPlaceholder")}
                clearLabel={t("common:clearSearch")}
                placeholder={t("searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm("")}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <FilterMenu className="gap-2" groups={flowboardFilterGroups} label={t("filters")} />

              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  {t("clearFilters")} ({activeFiltersCount})
                </Button>
              )}
            </div>

            <div className="flex flex-nowrap gap-2 ml-auto items-center shrink-0">
              <SegmentedTabs
                className="!gap-0"
                variant="secondary"
                value={view}
                onValueChange={(value) => setView(value as "kanban" | "list")}
                items={[
                  { value: "kanban", label: t("viewKanban"), icon: LayoutGrid },
                  { value: "list", label: t("viewList"), icon: ListIcon },
                ]}
              />
              {view === "kanban" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Settings2 className="h-4 w-4" />
                      {t("configureColumns")}
                      <span className="text-xs font-bold bg-muted px-1.5 py-0.5 rounded-full">
                        {visibleStatuses.length}/{statuses.length}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-3">{t("visibleColumns")}</h4>
                        <div className="space-y-2">
                          {statuses.map((status) => (
                            <div key={status.name} className="flex items-center space-x-2">
                              <Checkbox
                                id={status.name}
                                className="!min-h-0"
                                checked={isColumnVisible(status.name)}
                                onCheckedChange={() => toggleColumn(status.name)}
                              />
                              <label
                                htmlFor={status.name}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex flex-1 items-center gap-2"
                              >
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getStatusColor(status.name, statuses) }} />
                                {status.display_name}
                              </label>
                              <Badge variant="secondary" className="text-xs">
                                {groupedAppointments[status.name]?.length || 0}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {view === "list" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Columns3 className="h-4 w-4" />
                      {t("columnsButton")}
                      <span className="text-xs font-bold bg-muted px-1.5 py-0.5 rounded-full">
                        {listVisibleCount}/{listColumns.length}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-52 p-2">
                    <div className="flex items-center justify-between px-2 pb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("columnsTitle")}
                      </span>
                      <button
                        className="text-xs font-semibold text-destructive hover:underline"
                        onClick={resetListColumns}
                        type="button"
                      >
                        {t("reset")}
                      </button>
                    </div>
                    {listColumns.map((col) => {
                      const on = listVisibility[col.id] !== false;
                      const inputId = `flowboard-list-col-${col.id}`;
                      return (
                        <label
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                          key={col.id}
                          htmlFor={inputId}
                        >
                          <Checkbox
                            id={inputId}
                            className="!min-h-0"
                            checked={on}
                            onCheckedChange={() => toggleListColumn(col.id)}
                          />
                          <span>{col.header as string}</span>
                        </label>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={async () => {
                  const exportRows = view === "list" ? filteredListAppointments : filteredAppointments;
                  const filename = getAppointmentsCsvFilename({ view: "flowboard", date: selectedDate });
                  try {
                    const detailed = await fetchAppointmentsForCsv({
                      appointmentIds: exportRows.map((a) => a.id),
                    });
                    downloadAppointmentsCsv(detailed, filename);
                  } catch (err) {
                    console.error(err);
                    toast({
                      title: t("exportErrorTitle"),
                      description: t("exportErrorDescription"),
                    });
                    downloadAppointmentsCsv(exportRows, filename);
                  }
                }}
                disabled={(view === "list" ? filteredListAppointments : filteredAppointments).length === 0}
              >
                <Download className="h-4 w-4" />
                {t("export")}
              </Button>
            </div>
          </div>
        </div>

        <div className="px-5 md:hidden container">
          <MobileFlowboardHeader
            view={view}
            setView={setView}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            goToPreviousDay={goToPreviousDay}
            goToNextDay={goToNextDay}
            dateLabel={mobileDateLabel}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            vetOptions={professionalFilterOptions.map((vet) => ({ value: vet.id, label: vet.full_name }))}
            selectedVets={selectedVets}
            toggleVet={toggleVet}
            typeOptions={appointmentTypes.map((type) => ({ value: type.value, label: type.label }))}
            selectedTypes={selectedTypes}
            toggleType={toggleType}
            onlyTaxiDog={selectedDriverTypes.length > 0}
            setOnlyTaxiDog={(value: boolean) => setSelectedDriverTypes(value ? ["pickup", "dropoff"] : [])}
            statuses={statuses}
            isColumnVisible={isColumnVisible}
            toggleColumn={toggleColumn}
            columnCounts={Object.fromEntries(statuses.map((s) => [s.name, groupedAppointments[s.name]?.length || 0]))}
            getStatusColor={getStatusColor}
            activeFiltersCount={activeFiltersCount}
            clearFilters={clearFilters}
            canShowQuickStart={canShowQuickStart}
            clinicId={clinicId}
            isClinicLoading={isClinicLoading}
            onCreateAppointment={() => setIsCreateOpen(true)}
            onStartAttendance={() => setIsQuickStartOpen(true)}
          />
        </div>

        {isClinicLoading || isStatusesLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-6 w-1/3" />
            <div className="flex gap-4 overflow-x-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="min-w-[280px] flex-1 rounded-lg border bg-muted/40 p-4">
                  <Skeleton className="h-5 w-2/3 mb-2" />
                  <Skeleton className="h-4 w-full mb-4" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ))}
            </div>
          </div>
        ) : view === "list" ? (
          <FlowboardList
            appointments={filteredListAppointments}
            statuses={statuses}
            onRowClick={setSelectedAppointmentId}
            visibility={listVisibility}
          />
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCorners}
          >
            <div className="flex gap-3 overflow-x-auto pb-2 px-3 md:px-10 flowboard-scroll">
              {visibleStatuses.map((status) => (
                <DroppableStatusColumn
                  key={status.name}
                  status={status}
                  appointments={groupedAppointments[status.name] || []}
                  statuses={statuses}
                  professionals={veterinarians}
                  onAppointmentClick={setSelectedAppointmentId}
                  showAppointmentItemsOnCards={clinicFeatureFlags?.showAppointmentItemsOnCards === true}
                />
              ))}
            </div>

            {/* DragOverlay - preview durante drag */}
            <DragOverlay>
              {activeAppointment ? (
                <div className="max-w-[280px] rotate-2 cursor-grabbing pointer-events-none">
                  <AppointmentKanbanCard
                    appointment={activeAppointment}
                    statuses={statuses}
                    professionals={veterinarians}
                    showAppointmentItemsOnCards={clinicFeatureFlags?.showAppointmentItemsOnCards === true}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {selectedAppointmentId && (
          <AppointmentDetailsDialog
            appointmentId={selectedAppointmentId}
            open={!!selectedAppointmentId}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedAppointmentId(null);
              }
            }}
          />
        )}
      </div>
    </PermissionGuard>
  );
}
